import React, { useCallback, useEffect, useState } from "react";

import styles from "./DeleteAccount.module.css";
import Api from "../../utils/Api/Api";
import { hasSession } from "../../components/AuthModal/AuthGate";
import { TopBar } from "../../components/TopBar/TopBar";
import { Sheet } from "../../components/Sheet/Sheet";
import { endSession } from "../../utils/session";

const LOGOUT_AFTER_SECONDS = 5;

/**
 * Account deletion. Not a screen in the prototype; composed from its alert
 * card, fields, button and sheet so it sits in the same system.
 */
export const DeleteAccount = () => {
  const [reason, setReason] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [countdown, setCountdown] = useState(LOGOUT_AFTER_SECONDS);

  useEffect(() => {
    if (!hasSession()) {
      localStorage.setItem("returnToPage", "/delete-account");
      window.location.href = "/login";
    }
  }, []);

  const signOut = useCallback(() => {
    endSession("account_deletion");
    window.location.href = "/login";
  }, []);

  useEffect(() => {
    if (!done) return;
    if (countdown <= 0) {
      signOut();
      return;
    }
    const t = setTimeout(() => setCountdown((n) => n - 1), 1000);
    return () => clearTimeout(t);
  }, [done, countdown, signOut]);

  const remove = async () => {
    if (confirm !== "DELETE" || busy) return;
    setBusy(true);
    setError("");
    try {
      await Api.DeleteAccount();
      setDone(true);
    } catch (err) {
      setError(err?.response?.data?.error || "Account deletion request failed. Please try again.");
      setBusy(false);
    }
  };

  return (
    <div className={styles.page}>
      <TopBar title="Delete account" backTo="/account" />

      <Sheet open={done} onClose={() => {}} dismissible={false} label="Deletion requested">
        <p className={styles.sheetTitle}>Deletion request registered</p>
        <p className={styles.sheetBody}>
          Your account deletion request is being processed. You'll be signed out in{" "}
          <strong>{countdown}</strong> seconds.
        </p>
        <button type="button" className={styles.primary} onClick={signOut}>
          Sign out now
        </button>
      </Sheet>

      <div className={styles.shell}>
        <section className={styles.warning} role="alert">
          <span className={styles.warningIcon}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="9" />
              <path d="M12 8v4" />
              <path d="M12 16h.01" />
            </svg>
          </span>
          <div>
            <div className={styles.warningTitle}>This is permanent</div>
            <p className={styles.warningBody}>Deleting your account removes everything, including:</p>
            <ul className={styles.warningList}>
              <li>Your name, email, date of birth and gender</li>
              <li>Active subscriptions and UPI Autopay instructions</li>
              <li>Your coin balance, reset to 0</li>
              <li>Referral details and reward history</li>
              <li>Friend lists, notification logs and SMS parser records</li>
            </ul>
            <p className={styles.warningBody}>You won't be able to reactivate the account or recover any of it.</p>
          </div>
        </section>

        <div className={styles.fields}>
          <div>
            <label className={styles.label} htmlFor="del-reason">Why are you leaving? (optional)</label>
            <div className={styles.field}>
              <textarea
                id="del-reason"
                className={styles.textarea}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Tell us if there's anything we could do better…"
              />
            </div>
          </div>
          <div>
            <label className={styles.label} htmlFor="del-confirm">
              Type <strong>DELETE</strong> to confirm
            </label>
            <div className={styles.field}>
              <input
                id="del-confirm"
                className={styles.input}
                value={confirm}
                onChange={(e) => {
                  setConfirm(e.target.value);
                  setError("");
                }}
                placeholder="DELETE"
                autoCapitalize="characters"
              />
            </div>
          </div>
        </div>

        {error && <p className={styles.error} role="alert">{error}</p>}

        <button type="button" className={styles.danger} onClick={remove} disabled={confirm !== "DELETE" || busy}>
          {busy ? "Requesting deletion…" : "Permanently delete my account"}
        </button>
      </div>
    </div>
  );
};

export default DeleteAccount;
